package com.evoapps.evoshift.ui.roster

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Publish
import androidx.compose.material.icons.filled.Visibility
import androidx.compose.material.icons.filled.VisibilityOff
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.ui.util.monthInSentence
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.roster_publish_changes
import evoshift.composeapp.generated.resources.roster_unpublished_badge
import evoshift.composeapp.generated.resources.roster_unpublished_count
import evoshift.composeapp.generated.resources.roster_unpublished_hide
import evoshift.composeapp.generated.resources.roster_unpublished_removed
import com.evoapps.evoshift.domain.model.RemovedShift
import kotlinx.datetime.number
import evoshift.composeapp.generated.resources.roster_unpublished_show
import evoshift.composeapp.generated.resources.roster_unpublished_title
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.resources.pluralStringResource
import org.jetbrains.compose.resources.stringResource

/** SH-263: test tags of the unpublished changes: the builder's banner, the list's badge, the highlighted shifts and days. */
object UnpublishedTags {
    const val BANNER = "roster_unpublished_banner"
    const val BADGE = "roster_unpublished_badge"
    fun shift(id: String) = "roster_unpublished_shift_$id"
    fun day(date: LocalDate) = "roster_unpublished_day_$date"
    fun removed(shiftId: String) = "roster_unpublished_removed_$shiftId"
}

/**
 * SH-263: the shifts the builder highlights after "Show" on the banner (the changes the next
 * publish sends); empty while the highlight is off.
 */
val LocalUnpublishedHighlight = compositionLocalOf<Set<String>> { emptySet() }

/** SH-263: the edge of a highlighted shift and the tint of a phone's day with changes. */
internal val UnpublishedEdge = Color(0xFFF5B400)
internal val UnpublishedTint = Color(0xFFFFF4D6)

/**
 * SH-263: over a published month's grid, while it has changes the team does not see yet: "Masz
 * nieopublikowane zmiany w grafiku: listopad 2026", how many, "Opublikuj zmiany" (the builder's
 * own publish) and "Pokaż" / "Ukryj" (the changed shifts highlighted in the grid or the day list).
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
internal fun UnpublishedChangesBanner(
    year: Int,
    month: Int,
    count: Int,
    highlighted: Boolean,
    /** SH-263: published shifts deleted since: listed under the buttons while [highlighted]. */
    removed: List<RemovedShift> = emptyList(),
    /** The name of a removed shift's person (the team's; the record keeps only the id). */
    nameOf: (String) -> String? = { null },
    publishing: Boolean,
    onPublish: () -> Unit,
    onToggleHighlight: () -> Unit
) {
    FlowRow(
        modifier = Modifier
            .fillMaxWidth()
            .background(UnpublishedTint)
            .padding(horizontal = 16.dp, vertical = 8.dp)
            .testTag(UnpublishedTags.BANNER),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalArrangement = Arrangement.spacedBy(6.dp),
        itemVerticalAlignment = Alignment.CenterVertically
    ) {
        Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.padding(end = 4.dp)) {
            Box(Modifier.size(8.dp).clip(RoundedCornerShape(4.dp)).background(UnpublishedEdge))
            Spacer(Modifier.width(8.dp))
            Column {
                Text(
                    stringResource(Res.string.roster_unpublished_title, "${monthInSentence(month)} $year"),
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.Bold,
                    color = DeepNavy
                )
                Text(
                    pluralStringResource(Res.plurals.roster_unpublished_count, count, count),
                    style = MaterialTheme.typography.bodySmall,
                    color = DeepNavy.copy(alpha = 0.8f)
                )
            }
        }
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            Button(
                onClick = onPublish,
                enabled = !publishing,
                modifier = Modifier.handCursor(),
                shape = RoundedCornerShape(8.dp),
                contentPadding = PaddingValues(horizontal = 12.dp, vertical = 6.dp),
                colors = ButtonDefaults.buttonColors(containerColor = DeepNavy, contentColor = Color.White)
            ) {
                ButtonContent(Icons.Default.Publish, stringResource(Res.string.roster_publish_changes))
            }
            OutlinedButton(
                onClick = onToggleHighlight,
                modifier = Modifier.handCursor(),
                shape = RoundedCornerShape(8.dp),
                contentPadding = PaddingValues(horizontal = 12.dp, vertical = 6.dp),
                colors = ButtonDefaults.outlinedButtonColors(contentColor = DeepNavy)
            ) {
                ButtonContent(
                    if (highlighted) Icons.Default.VisibilityOff else Icons.Default.Visibility,
                    stringResource(if (highlighted) Res.string.roster_unpublished_hide else Res.string.roster_unpublished_show)
                )
            }
        }
        if (highlighted && removed.isNotEmpty()) {
            Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                removed.sortedBy { it.start }.forEach { RemovedLine(it, it.userId?.let(nameOf) ?: it.userDisplayName) }
            }
        }
    }
}

/** SH-263: "Usunięto: 3.11 Kasia Kelner 07:00–15:00" (an unassigned shift: without a name). */
@Composable
private fun RemovedLine(removed: RemovedShift, name: String?) {
    Text(
        stringResource(Res.string.roster_unpublished_removed, removedShiftSummary(removed, name?.takeIf { it.isNotBlank() })),
        style = MaterialTheme.typography.bodySmall,
        color = DeepNavy,
        modifier = Modifier.testTag(UnpublishedTags.removed(removed.shiftId))
    )
}

/** SH-263: "3.11 Kasia Kelner 07:00–15:00": the day, the person (if any), the planned times. */
fun removedShiftSummary(removed: RemovedShift, name: String? = removed.userDisplayName?.takeIf { removed.userId != null && it.isNotBlank() }): String {
    val date = runCatching { LocalDate.parse(removed.date) }.getOrNull()
    val day = date?.let { "${it.day}.${it.month.number}" } ?: removed.date
    val times = "${removed.start.substringAfter('T').take(5)}–${removed.end.substringAfter('T').take(5)}"
    return listOfNotNull(day, name, times).joinToString(" ")
}

@Composable
private fun ButtonContent(icon: ImageVector, label: String) {
    Icon(icon, contentDescription = null, modifier = Modifier.size(18.dp))
    Spacer(Modifier.width(6.dp))
    Text(label, fontWeight = FontWeight.Bold, maxLines = 1)
}

/** SH-263: a rosters list card's "Nieopublikowane zmiany" (a published month changed since). */
@Composable
internal fun UnpublishedBadge(modifier: Modifier = Modifier) {
    Box(
        modifier
            .clip(RoundedCornerShape(6.dp))
            .background(UnpublishedTint)
            .border(1.dp, UnpublishedEdge, RoundedCornerShape(6.dp))
            .padding(horizontal = 8.dp, vertical = 2.dp)
            .testTag(UnpublishedTags.BADGE)
    ) {
        Text(
            stringResource(Res.string.roster_unpublished_badge),
            style = MaterialTheme.typography.labelMedium,
            fontWeight = FontWeight.SemiBold,
            color = DeepNavy
        )
    }
}
